import { FormGroup } from '@angular/forms';
import { IDateTimeRangeForm } from '@shared/components/date-time-range-fields/date-time-range-fields.interface';

export type TBriefBasicsDatesValue = FormGroup<IDateTimeRangeForm>['value'];

// This step only ever opens because of a start/end error - so it always recomputes a sensible
// default rather than echoing back whatever (possibly invalid, e.g. end before start) raw values
// the brief produced: a known start always wins and derives end (end-of-day, or +24h if start is
// already 23:59); only when start is missing does a known end derive start (start-of-day, or -24h
// if end is already 00:00). startDate/startTime (and endDate/endTime) are always the same instant
// here - see brief-wizard-dialog.component.ts's currentBasicsDatesValue - so only one of each pair
// is read. Deliberately local to this step, not the shared date-time-range-fields component/
// DateTimeRangeFieldsComponent - the general campaign date picker must keep showing whatever the
// user actually entered.
export function resolveDefaultBasicsDatesValue(value: TBriefBasicsDatesValue): TBriefBasicsDatesValue {
  const { endDate, startDate } = value;

  if (startDate) {
    const defaultEnd = endOfDayOrNextDay(startDate);

    return { endDate: defaultEnd, endTime: defaultEnd, startDate, startTime: startDate };
  }

  if (endDate) {
    const defaultStart = startOfDayOrPreviousDay(endDate);

    return { endDate, endTime: endDate, startDate: defaultStart, startTime: defaultStart };
  }

  return value;
}

// Uses setDate (calendar-day arithmetic), not a fixed 24h offset added to getTime() - a fixed
// offset breaks across a DST transition (e.g. Europe/Berlin 2026-03-28 23:59 + 24h lands on
// 2026-03-30 00:59, shifting a whole day), while setDate always lands on the same wall-clock time
// the next/previous calendar day regardless of any DST shift in between.
function endOfDayOrNextDay(date: Date): Date {
  if (date.getHours() === 23 && date.getMinutes() === 59) {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);

    return nextDay;
  }

  const end = new Date(date);
  end.setHours(23, 59, 0, 0);

  return end;
}

function startOfDayOrPreviousDay(date: Date): Date {
  if (date.getHours() === 0 && date.getMinutes() === 0) {
    const previousDay = new Date(date);
    previousDay.setDate(previousDay.getDate() - 1);

    return previousDay;
  }

  const start = new Date(date);
  start.setHours(0, 0, 0, 0);

  return start;
}
